/**
 * CartProvider
 *
 * Note: The main route configured in the admin panel should not be 'exact'
 *
 * Usage - Default:
 * <CartProvider disableMeta />
 *
 * Usage - Mini cart (meta disabled by default):
 * <CartProvider miniCart />
 *
 * Usage - overriding individual components, while maintaining logic and structure:
 * <CartProvider
 *  disableMeta
 *  PointsValue={CustomComponentPointsValue}
 *  MiniCart={CustomComponentMiniCart}
 *  ...
 * />
 *
 * Usage - Overriding all logic and internal components:
 * <CartProvider disableMeta>
 *   <CustomMiniCart />
 *   ...
 * </CartProvider>
 *
 *
 * disableMeta prop can be used to disable Helmet from applying meta tags. This
 * is useful if this component is included on a page (or in the header) and not
 * in its own page container.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import PropIsValidReactElement from '_platform/src/utils/PropIsValidReactElement';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Route, withRouter } from 'react-router-dom';

// Style and SEO
import Helmet from 'react-helmet';
import { withTheme } from '@smooth-ui/core-sc';
import { withSettings } from '../../containers/WebApp/SettingsContext';

import injectReducer from '_platform/src/utils/injectReducer';
import injectSaga from '_platform/src/utils/injectSaga';
import {
  cartCheckAndContinue,
  cartItemRemoveRequest,
  cartUpdateRewardsRequest,
} from './actions';
import {
  countriesRequest,
  userProfileRequest,
} from '_platform/src/containers/App/actions';
import { customerRepsRequest } from '../WebApp/actions';
import reducer from './reducer';
import saga from './saga';
import { selectCartMessage, selectIsSubmitting } from './selectors';
import { selectCart, selectCustomerReps } from '../WebApp/selectors';
import {
  selectCountries,
  selectCurrentUser,
  selectUserProfile,
} from '_platform/src/containers/App/selectors';

import { trailingSlashIt } from '_platform/src/utils/utilities';
import LoadAsync from '_platform/src/utils/LoadAsync';
const CartDefault = LoadAsync(() =>
  import(/* webpackChunkName: "cart" */ '../../components/Cart/Cart')
);
const CartDeliveryDefault = LoadAsync(() =>
  import(
    /* webpackChunkName: "cartDelivery" */ '../../components/Cart/CartDelivery'
  )
);
const CartConfirmDefault = LoadAsync(() =>
  import(
    /* webpackChunkName: "cartConfirm" */ '../../components/Cart/CartConfirm'
  )
);
const CartThankYouDefault = LoadAsync(() =>
  import(
    /* webpackChunkName: "cartThankYou" */ '../../components/Cart/CartThankYou'
  )
);

class CartProvider extends Component {
  Meta = () =>
    (!this.props.disableMeta || !this.props.isMiniCart) && (
      <Helmet>
        <title>Cart</title>
        {/*<meta name="description" content="Page description" />*/}
      </Helmet>
    );

  render() {
    if (
      !this.props.cart ||
      this.props.cart.isLoading ||
      this.props.currentUser.disableClaiming
    ) {
      return null;
    }

    const parentPagePath = trailingSlashIt(this.props.match.url);

    // Component overrides
    const {
      Cart = CartDefault,
      CartDelivery = CartDeliveryDefault,
      CartConfirm = CartConfirmDefault,
      CartThankYou = CartThankYouDefault,
    } = this.props;

    if (this.props.children) {
      return React.Children.map(this.props.children, childElement =>
        React.cloneElement(childElement, {
          ...this.props,
          parentPagePath,
        })
      );
    }

    // Used for the mini cart as well as the main cart
    const CartComponentWithProps = (
      <Cart
        cart={this.props.cart}
        cartMessage={this.props.cartMessage}
        CartIcon={this.props.CartIcon}
        PointsValue={this.props.PointsValue}
        StyledCartContainer={this.props.StyledCartContainer}
        StyledCartContent={this.props.StyledCartContent}
        StyledMiniCartIcon={this.props.StyledMiniCartIcon}
        isSubmitting={this.props.isSubmitting}
        isMiniCart={this.props.isMiniCart}
        onCartCheckAndContinue={this.props.onCartCheckAndContinue}
        onCartItemRemoveRequest={this.props.onCartItemRemoveRequest}
        onCartUpdateRewards={this.props.onCartUpdateRewards}
        settings={this.props.settings}
      />
    );

    // If it's the mini cart, display regardless of route
    if (this.props.isMiniCart) {
      return CartComponentWithProps;
    }

    return (
      <Route
        path={`${trailingSlashIt(
          this.props.settings.settingsApp.cartPage
        )}:step?`}
        render={routeProps => {
          const pageParams = routeProps.match.params;

          switch (pageParams.step) {
            case 'delivery':
              return (
                <CartDelivery
                  cart={this.props.cart}
                  cartMessage={this.props.cartMessage}
                  CartDeliveryAddressIntro={this.props.CartDeliveryAddressIntro}
                  StyledCartContainer={this.props.StyledCartContainer}
                  StyledCartContent={this.props.StyledCartContent}
                  countriesData={this.props.countriesData}
                  customerReps={this.props.customerReps}
                  isSubmitting={this.props.isSubmitting}
                  onCartCheckAndContinue={this.props.onCartCheckAndContinue}
                  onCountriesRequest={this.props.onCountriesRequest}
                  onCustomerRepsRequest={this.props.onCustomerRepsRequest}
                  onUserProfileRequest={this.props.onUserProfileRequest}
                  parentPagePath={parentPagePath}
                  settings={this.props.settings}
                  userProfile={this.props.userProfile}
                />
              );

            case 'confirm':
              return (
                <CartConfirm
                  cart={this.props.cart}
                  cartMessage={this.props.cartMessage}
                  PointsValue={this.props.PointsValue}
                  StyledCartContainer={this.props.StyledCartContainer}
                  StyledCartContent={this.props.StyledCartContent}
                  CartConfirmMessage={this.props.CartConfirmMessage}
                  isSubmitting={this.props.isSubmitting}
                  onCartCheckAndContinue={this.props.onCartCheckAndContinue}
                  onUserProfileRequest={this.props.onUserProfileRequest}
                  parentPagePath={parentPagePath}
                  settings={this.props.settings}
                  userProfile={this.props.userProfile}
                />
              );

            case 'thank-you':
              return (
                <CartThankYou
                  CartThankYouMessage={this.props.CartThankYouMessage}
                  StyledCartContainer={this.props.StyledCartContainer}
                  StyledCartContent={this.props.StyledCartContent}
                  settings={this.props.settings}
                />
              );

            default:
              return CartComponentWithProps;
          }
        }}
      />
    );
  }
}

CartProvider.propTypes = {
  Cart: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  cart: PropTypes.object,
  CartConfirm: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  CartConfirmMessage: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  CartDelivery: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  CartDeliveryAddressIntro: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  CartIcon: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  cartMessage: PropTypes.string,
  CartThankYou: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  CartThankYouMessage: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  children: PropTypes.element,
  countriesData: PropTypes.object,
  currentUser: PropTypes.object.isRequired,
  customerReps: PropTypes.object,
  disableMeta: PropTypes.bool,
  isMiniCart: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  location: PropTypes.object,
  match: PropTypes.object,
  MiniCart: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  onCartCheckAndContinue: PropTypes.func.isRequired,
  onCartItemRemoveRequest: PropTypes.func.isRequired,
  onCartUpdateRewards: PropTypes.func.isRequired,
  onCountriesRequest: PropTypes.func.isRequired,
  onCustomerRepsRequest: PropTypes.func.isRequired,
  onUserProfileRequest: PropTypes.func.isRequired,
  PointsValue: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  settings: PropTypes.object.isRequired,
  StyledCartContainer: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  StyledCartContent: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  StyledMiniCartIcon: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  theme: PropTypes.object.isRequired,
  userProfile: PropTypes.object,
};

CartProvider.defaultProps = {
  cart: undefined,
  cartMessage: undefined,
  children: undefined,
  countriesData: undefined,
  customerReps: undefined,
  disableMeta: false,
  location: {},
  match: {},
  isSubmitting: false,
  isMiniCart: false,
  userProfile: undefined,
};

const mapStateToProps = createStructuredSelector({
  cart: selectCart(),
  cartMessage: selectCartMessage(),
  countriesData: selectCountries(),
  currentUser: selectCurrentUser(),
  customerReps: selectCustomerReps(),
  isSubmitting: selectIsSubmitting(),
  userProfile: selectUserProfile(),
});

function mapDispatchToProps(dispatch, props) {
  return {
    dispatch,
    onCartCheckAndContinue: (currentStep, nextPagePath, values) =>
      dispatch(cartCheckAndContinue(currentStep, nextPagePath, values)),
    onCartItemRemoveRequest: cartItemId =>
      dispatch(cartItemRemoveRequest(cartItemId)),
    onCartUpdateRewards: values => dispatch(cartUpdateRewardsRequest(values)),
    onCountriesRequest: () => dispatch(countriesRequest()),
    onCustomerRepsRequest: () => dispatch(customerRepsRequest()),
    onUserProfileRequest: () => dispatch(userProfileRequest()),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withReducer = injectReducer({ key: 'cart', reducer });
const withSaga = injectSaga({ key: 'cart', saga }); // Prevent saga cancel on component unmount

export default compose(
  withRouter,
  withTheme,
  withSettings,
  withReducer,
  withSaga,
  withConnect
)(CartProvider);
